// Helpdesk mock data
export interface Ticket {
  id: string;
  ticketNumber: string;
  subject: string;
  description: string;
  status: 'new' | 'open' | 'in-progress' | 'pending' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  assigneeId?: string;
  assigneeName?: string;
  tags: string[];
  createdAt: Date;
  updatedAt: Date;
  resolvedAt?: Date;
  firstResponseAt?: Date;
  slaDeadline?: Date;
}

export interface TicketComment {
  id: string;
  ticketId: string;
  authorId: string;
  authorName: string;
  authorType: 'agent' | 'customer';
  content: string;
  isInternal: boolean;
  attachments?: string[];
  createdAt: Date;
}

export interface KnowledgeArticle {
  id: string;
  title: string;
  slug: string;
  content: string;
  category: string;
  tags: string[];
  authorId: string;
  authorName: string;
  status: 'draft' | 'published' | 'archived';
  viewCount: number;
  helpfulCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export const ticketCategories = [
  'Technical Support',
  'Billing',
  'Account',
  'Feature Request',
  'Bug Report',
  'General Inquiry',
];

export const mockTickets: Ticket[] = [
  {
    id: 'ticket-1',
    ticketNumber: 'TKT-2024-001',
    subject: 'Unable to login to dashboard',
    description: 'I am getting an error message when trying to login. The page shows "Invalid credentials" even though I am sure my password is correct.',
    status: 'in-progress',
    priority: 'high',
    category: 'Technical Support',
    customerId: 'cust-1',
    customerName: 'John Smith',
    customerEmail: 'john.smith@email.com',
    assigneeId: 'u22',
    assigneeName: 'Tina Turner',
    tags: ['login', 'authentication', 'urgent'],
    createdAt: new Date('2024-01-20T09:00:00'),
    updatedAt: new Date('2024-01-21T10:30:00'),
    firstResponseAt: new Date('2024-01-20T09:45:00'),
    slaDeadline: new Date('2024-01-21T17:00:00'),
  },
  {
    id: 'ticket-2',
    ticketNumber: 'TKT-2024-002',
    subject: 'Billing discrepancy on invoice',
    description: 'My latest invoice shows charges for services I did not use. Please review and correct.',
    status: 'pending',
    priority: 'medium',
    category: 'Billing',
    customerId: 'cust-2',
    customerName: 'Sarah Johnson',
    customerEmail: 'sarah.j@email.com',
    assigneeId: 'u22',
    assigneeName: 'Tina Turner',
    tags: ['billing', 'invoice', 'review'],
    createdAt: new Date('2024-01-19T14:30:00'),
    updatedAt: new Date('2024-01-20T16:00:00'),
    firstResponseAt: new Date('2024-01-19T15:15:00'),
  },
  {
    id: 'ticket-3',
    ticketNumber: 'TKT-2024-003',
    subject: 'Request for API documentation',
    description: 'We are looking to integrate your API into our system. Can you provide detailed documentation?',
    status: 'resolved',
    priority: 'low',
    category: 'General Inquiry',
    customerId: 'cust-3',
    customerName: 'Michael Brown',
    customerEmail: 'mbrown@email.com',
    assigneeId: 'u22',
    assigneeName: 'Tina Turner',
    tags: ['api', 'documentation', 'integration'],
    createdAt: new Date('2024-01-18T11:00:00'),
    updatedAt: new Date('2024-01-19T09:30:00'),
    resolvedAt: new Date('2024-01-19T09:30:00'),
    firstResponseAt: new Date('2024-01-18T11:45:00'),
  },
  {
    id: 'ticket-4',
    ticketNumber: 'TKT-2024-004',
    subject: 'Application crashes on file upload',
    description: 'When I try to upload files larger than 10MB, the application crashes. This happens consistently.',
    status: 'new',
    priority: 'urgent',
    category: 'Bug Report',
    customerId: 'cust-4',
    customerName: 'Emily Davis',
    customerEmail: 'emily.d@email.com',
    tags: ['bug', 'upload', 'crash', 'critical'],
    createdAt: new Date('2024-01-21T08:00:00'),
    updatedAt: new Date('2024-01-21T08:00:00'),
    slaDeadline: new Date('2024-01-21T12:00:00'),
  },
  {
    id: 'ticket-5',
    ticketNumber: 'TKT-2024-005',
    subject: 'Feature request: Dark mode',
    description: 'It would be great if you could add a dark mode option to the dashboard. Many of us work late hours.',
    status: 'open',
    priority: 'low',
    category: 'Feature Request',
    customerId: 'cust-5',
    customerName: 'David Wilson',
    customerEmail: 'dwilson@email.com',
    tags: ['feature', 'ui', 'dark-mode'],
    createdAt: new Date('2024-01-17T16:00:00'),
    updatedAt: new Date('2024-01-18T10:00:00'),
    firstResponseAt: new Date('2024-01-18T10:00:00'),
  },
  {
    id: 'ticket-6',
    ticketNumber: 'TKT-2024-006',
    subject: 'Password reset not working',
    description: 'I requested a password reset but never received the email. I have checked my spam folder.',
    status: 'in-progress',
    priority: 'high',
    category: 'Account',
    customerId: 'cust-6',
    customerName: 'Lisa Anderson',
    customerEmail: 'lisa.a@email.com',
    assigneeId: 'u22',
    assigneeName: 'Tina Turner',
    tags: ['password', 'email', 'account'],
    createdAt: new Date('2024-01-20T13:00:00'),
    updatedAt: new Date('2024-01-21T09:00:00'),
    firstResponseAt: new Date('2024-01-20T13:30:00'),
  },
  {
    id: 'ticket-7',
    ticketNumber: 'TKT-2024-007',
    subject: 'How to export data?',
    description: 'I need to export my data to CSV format. Cannot find the option in the dashboard.',
    status: 'closed',
    priority: 'low',
    category: 'General Inquiry',
    customerId: 'cust-7',
    customerName: 'Robert Taylor',
    customerEmail: 'rtaylor@email.com',
    assigneeId: 'u22',
    assigneeName: 'Tina Turner',
    tags: ['export', 'data', 'csv'],
    createdAt: new Date('2024-01-15T10:00:00'),
    updatedAt: new Date('2024-01-16T14:00:00'),
    resolvedAt: new Date('2024-01-16T11:00:00'),
    firstResponseAt: new Date('2024-01-15T10:30:00'),
  },
  {
    id: 'ticket-8',
    ticketNumber: 'TKT-2024-008',
    subject: 'Upgrade subscription plan',
    description: 'I would like to upgrade from Basic to Pro plan. What are the steps?',
    status: 'open',
    priority: 'medium',
    category: 'Billing',
    customerId: 'cust-8',
    customerName: 'Jennifer Martinez',
    customerEmail: 'jmartinez@email.com',
    tags: ['upgrade', 'subscription', 'billing'],
    createdAt: new Date('2024-01-21T07:30:00'),
    updatedAt: new Date('2024-01-21T07:30:00'),
  },
];

export const mockTicketComments: TicketComment[] = [
  {
    id: 'comment-1',
    ticketId: 'ticket-1',
    authorId: 'cust-1',
    authorName: 'John Smith',
    authorType: 'customer',
    content: 'I am getting an error message when trying to login. The page shows "Invalid credentials" even though I am sure my password is correct.',
    isInternal: false,
    createdAt: new Date('2024-01-20T09:00:00'),
  },
  {
    id: 'comment-2',
    ticketId: 'ticket-1',
    authorId: 'u22',
    authorName: 'Tina Turner',
    authorType: 'agent',
    content: 'Hi John, I am sorry to hear you are having trouble logging in. Can you try clearing your browser cache and cookies, then try again?',
    isInternal: false,
    createdAt: new Date('2024-01-20T09:45:00'),
  },
  {
    id: 'comment-3',
    ticketId: 'ticket-1',
    authorId: 'cust-1',
    authorName: 'John Smith',
    authorType: 'customer',
    content: 'I tried that but still getting the same error.',
    isInternal: false,
    createdAt: new Date('2024-01-20T10:30:00'),
  },
  {
    id: 'comment-4',
    ticketId: 'ticket-1',
    authorId: 'u22',
    authorName: 'Tina Turner',
    authorType: 'agent',
    content: 'Checking with the dev team about this issue.',
    isInternal: true,
    createdAt: new Date('2024-01-20T11:00:00'),
  },
  {
    id: 'comment-5',
    ticketId: 'ticket-1',
    authorId: 'u22',
    authorName: 'Tina Turner',
    authorType: 'agent',
    content: 'I have reset your account. Please try using the "Forgot Password" link to set a new password.',
    isInternal: false,
    createdAt: new Date('2024-01-21T10:30:00'),
  },
];

export const mockKnowledgeArticles: KnowledgeArticle[] = [
  {
    id: 'article-1',
    title: 'Getting Started Guide',
    slug: 'getting-started-guide',
    content: `# Getting Started Guide

Welcome to our platform! This guide will help you get up and running quickly.

## Creating Your Account
1. Visit our signup page
2. Enter your email and password
3. Verify your email address

## Setting Up Your Profile
After logging in, navigate to Settings > Profile to complete your setup.

## Need Help?
Contact our support team anytime!`,
    category: 'Getting Started',
    tags: ['onboarding', 'basics', 'setup'],
    authorId: 'u22',
    authorName: 'Tina Turner',
    status: 'published',
    viewCount: 1245,
    helpfulCount: 892,
    createdAt: new Date('2023-06-15'),
    updatedAt: new Date('2024-01-10'),
  },
  {
    id: 'article-2',
    title: 'How to Reset Your Password',
    slug: 'how-to-reset-password',
    content: `# How to Reset Your Password

If you have forgotten your password, follow these steps:

1. Go to the login page
2. Click "Forgot Password"
3. Enter your email address
4. Check your inbox for the reset link
5. Click the link and enter your new password

**Note:** The reset link expires after 24 hours.`,
    category: 'Account',
    tags: ['password', 'security', 'account'],
    authorId: 'u22',
    authorName: 'Tina Turner',
    status: 'published',
    viewCount: 3456,
    helpfulCount: 2890,
    createdAt: new Date('2023-07-20'),
    updatedAt: new Date('2024-01-05'),
  },
  {
    id: 'article-3',
    title: 'Understanding Your Invoice',
    slug: 'understanding-your-invoice',
    content: `# Understanding Your Invoice

This article explains the different sections of your invoice.

## Invoice Header
Contains your account information and invoice number.

## Line Items
Lists all services and their costs.

## Payment Terms
Our standard payment terms are Net 30.`,
    category: 'Billing',
    tags: ['billing', 'invoice', 'payment'],
    authorId: 'u22',
    authorName: 'Tina Turner',
    status: 'published',
    viewCount: 987,
    helpfulCount: 654,
    createdAt: new Date('2023-08-10'),
    updatedAt: new Date('2023-12-15'),
  },
  {
    id: 'article-4',
    title: 'API Authentication Guide',
    slug: 'api-authentication-guide',
    content: `# API Authentication Guide

Learn how to authenticate with our API.

## Getting Your API Key
Navigate to Settings > API to generate your key.

## Making Authenticated Requests
Include your API key in the Authorization header.

\`\`\`
Authorization: Bearer YOUR_API_KEY
\`\`\``,
    category: 'Developers',
    tags: ['api', 'authentication', 'developers'],
    authorId: 'u4',
    authorName: 'Bob Williams',
    status: 'published',
    viewCount: 2345,
    helpfulCount: 1890,
    createdAt: new Date('2023-09-05'),
    updatedAt: new Date('2024-01-15'),
  },
  {
    id: 'article-5',
    title: 'Exporting Your Data',
    slug: 'exporting-your-data',
    content: `# Exporting Your Data

You can export your data in multiple formats.

## Available Formats
- CSV
- Excel (XLSX)
- JSON

## How to Export
1. Go to Settings > Data
2. Click "Export"
3. Select your format
4. Download your file`,
    category: 'Data',
    tags: ['export', 'data', 'csv', 'download'],
    authorId: 'u22',
    authorName: 'Tina Turner',
    status: 'published',
    viewCount: 1567,
    helpfulCount: 1234,
    createdAt: new Date('2023-10-12'),
    updatedAt: new Date('2024-01-08'),
  },
  {
    id: 'article-6',
    title: 'Troubleshooting File Uploads',
    slug: 'troubleshooting-file-uploads',
    content: `# Troubleshooting File Uploads

Having issues uploading files? Check these common solutions.

## File Size Limits
Maximum file size is 50MB per file.

## Supported Formats
We support: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, GIF

## Common Issues
- Check your internet connection
- Ensure the file is not corrupted
- Try a different browser`,
    category: 'Troubleshooting',
    tags: ['upload', 'files', 'troubleshooting'],
    authorId: 'u22',
    authorName: 'Tina Turner',
    status: 'published',
    viewCount: 876,
    helpfulCount: 543,
    createdAt: new Date('2023-11-20'),
    updatedAt: new Date('2024-01-12'),
  },
];

// Helpdesk stats
export const helpdeskStats = {
  openTickets: 5,
  resolvedToday: 3,
  avgResponseTime: 45, // minutes
  avgResolutionTime: 480, // minutes (8 hours)
  customerSatisfaction: 4.6,
  slaCompliance: 92,
  ticketsThisWeek: 15,
  ticketsChange: 12.5,
};

// Tickets by status
export const ticketsByStatus = [
  { status: 'New', count: 1, color: 'blue' },
  { status: 'Open', count: 2, color: 'cyan' },
  { status: 'In Progress', count: 2, color: 'yellow' },
  { status: 'Pending', count: 1, color: 'orange' },
  { status: 'Resolved', count: 1, color: 'green' },
  { status: 'Closed', count: 1, color: 'gray' },
];

// Tickets by priority
export const ticketsByPriority = [
  { priority: 'Urgent', count: 1, color: 'red' },
  { priority: 'High', count: 2, color: 'orange' },
  { priority: 'Medium', count: 2, color: 'yellow' },
  { priority: 'Low', count: 3, color: 'green' },
];

// Tickets by category
export const ticketsByCategory = [
  { category: 'Technical Support', count: 2 },
  { category: 'Billing', count: 2 },
  { category: 'Account', count: 1 },
  { category: 'Feature Request', count: 1 },
  { category: 'Bug Report', count: 1 },
  { category: 'General Inquiry', count: 1 },
];

// Knowledge base categories
export const knowledgeCategories = [
  { name: 'Getting Started', count: 5, icon: 'rocket' },
  { name: 'Account', count: 8, icon: 'user' },
  { name: 'Billing', count: 6, icon: 'receipt' },
  { name: 'Developers', count: 12, icon: 'code' },
  { name: 'Data', count: 4, icon: 'database' },
  { name: 'Troubleshooting', count: 10, icon: 'tool' },
];
